import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, AlertTriangle, TrendingUp, Check, X as XIcon, Clock, Trash2, Share2, Download } from "lucide-react";
import Navbar from "@/components/Navbar";
import { S, KatanaDivider, SamuraiBackground } from "@/components/samurai/theme";
import { RoninIcon } from "@/components/samurai/RoninIcons";
import { RoninGenerateFX } from "@/components/samurai/RoninFX";
import BetSlipExportCard from "@/components/BetSlipExportCard";
import { exportNodeToPng } from "@/lib/exportImage";
import {
  saveLocal as saveSlipLocal,
  removeLocal as removeSlipLocal,
  findMissingOnServer,
  reconcileLocalWithServer,
} from "@/lib/roninHistory";
import { getSession } from "@/components/AccessGate";
import { playSfx } from "@/lib/audio";
import { usePremium } from "@/contexts/AuthContext";
import useDailyUsage from "@/hooks/useDailyUsage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build axios config with the X-Access-Code header so the backend can
// scope ronin slips to the current user.
//
// Resolution order for the owner identifier:
//   1. ``session.code`` — the access code from AccessGate (preferred)
//   2. ``localStorage.bc_device_id`` — the per-device UUID created at
//      first login (fallback for admin sessions, edge cases, sessions
//      restored from cache where ``.code`` is missing)
//   3. ``"GUEST-<random>"`` — generated and persisted on the spot if
//      both above fail (so we NEVER block a save with 401)
//
// The code is *always* uppercased and namespaced so two different users
// on the same device with different sessions still get distinct owners.
function _resolveOwnerCode() {
  try {
    const sess = getSession();
    if (sess?.code) return String(sess.code).toUpperCase();
  } catch { /* ignore */ }
  try {
    const dev = localStorage.getItem("bc_device_id");
    if (dev) return ("DEV-" + dev).toUpperCase();
  } catch { /* ignore */ }
  // Last-ditch fallback — generate a stable id and persist it so the
  // user keeps the same identity across reloads in this browser.
  try {
    let g = localStorage.getItem("bc_ronin_guest_id");
    if (!g) {
      g = "GUEST-" + (crypto.randomUUID?.() || Math.random().toString(36).slice(2));
      localStorage.setItem("bc_ronin_guest_id", g);
    }
    return g.toUpperCase();
  } catch { /* ignore */ }
  return "GUEST-FALLBACK";
}

function _ownerHeaders() {
  return { headers: { "X-Access-Code": _resolveOwnerCode() } };
}

const COACHES = [
  {
    id: "kurosawa", label: "KUROSAWA", sub: "Il Ronin Conservativo",
    color: "#9DF6FF", glow: "rgba(157, 246, 255, 0.45)",
    tag: "quota leg 1.20 – 1.40 · prob ≥ 78%",
  },
  {
    id: "mifune", label: "MIFUNE", sub: "Il Ronin Bilanciato",
    color: "#F6C643", glow: "rgba(246, 198, 67, 0.45)",
    tag: "quota leg 1.35 – 1.60 · prob ≥ 75%",
  },
  {
    id: "naruse", label: "NARUSE", sub: "Il Ronin Spietato",
    color: "#FF4B5C", glow: "rgba(255, 75, 92, 0.45)",
    tag: "quota leg 1.45 – 2.20 · prob ≥ 70%",
  },
];

function todayLocalISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function CoachCard({ coach, active, targetOdds, setTargetOdds, onGenerate, loading, disabled }) {
  return (
    <div
      data-testid={`ronin-coach-${coach.id}`}
      style={{
        position: "relative",
        background: active
          ? `linear-gradient(135deg, rgba(10,12,14,0.95) 0%, ${coach.color}11 50%, rgba(10,12,14,0.95) 100%)`
          : "rgba(12,14,16,0.88)",
        border: `1px solid ${active ? coach.color : S.hairline2}`,
        boxShadow: active
          ? `0 0 24px -4px ${coach.glow}, inset 0 0 18px rgba(0,0,0,0.5)`
          : "none",
        padding: "1.1rem 1rem 1rem",
        transition: "all 220ms ease",
        display: "flex", flexDirection: "column", gap: "0.75rem",
        cursor: disabled ? "wait" : "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
        <RoninIcon coach={coach.id} size={38} color={coach.color} />
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "1.05rem", letterSpacing: "0.25em", color: coach.color,
            textShadow: active ? `0 0 10px ${coach.color}` : "none",
          }}>
            {coach.label}
          </div>
          <div style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem",
            color: S.sub, fontStyle: "italic",
          }}>{coach.sub}</div>
        </div>
      </div>
      <div style={{
        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
        color: S.sub, letterSpacing: "0.06em",
      }}>{coach.tag}</div>

      {/* Slider — animated thumb + neon glow + running value */}
      <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.68rem", letterSpacing: "0.22em", color: S.sub,
            textTransform: "uppercase",
          }}>Quota target</span>
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontWeight: 800,
            fontSize: "1.2rem", color: coach.color,
            textShadow: `0 0 10px ${coach.glow}`,
            transition: "color 150ms ease",
          }}>{Number(targetOdds).toFixed(1)}x</span>
        </div>
        <input
          data-testid={`ronin-slider-${coach.id}`}
          type="range" min={2} max={100} step={0.5}
          value={targetOdds}
          onChange={(e) => setTargetOdds(parseFloat(e.target.value))}
          className="ronin-slider"
          style={{
            WebkitAppearance: "none", appearance: "none",
            width: "100%", height: 4,
            background: `linear-gradient(to right, ${coach.color} 0%, ${coach.color} ${((targetOdds - 2) / 98) * 100}%, ${S.hairline2} ${((targetOdds - 2) / 98) * 100}%, ${S.hairline2} 100%)`,
            outline: "none", cursor: "pointer",
            borderRadius: 2,
            // Custom thumb defined in global CSS block on this page
          }}
        />
        <div style={{
          display: "flex", justifyContent: "space-between",
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem",
          color: S.sub, opacity: 0.7,
        }}>
          <span>2x</span><span>50x</span><span>100x</span>
        </div>
      </div>

      <button
        data-testid={`ronin-generate-${coach.id}`}
        onClick={onGenerate}
        disabled={loading || disabled}
        style={{
          marginTop: "0.25rem",
          padding: "0.7rem 1rem",
          background: active ? `${coach.color}18` : "transparent",
          border: `1px solid ${coach.color}`,
          color: coach.color,
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          fontSize: "0.78rem", letterSpacing: "0.28em",
          textTransform: "uppercase",
          cursor: loading ? "wait" : "pointer",
          textShadow: `0 0 8px ${coach.glow}`,
          boxShadow: active ? `0 0 14px -4px ${coach.glow}` : "none",
          transition: "all 200ms ease",
        }}
      >
        {loading && active ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
            <Loader2 size={12} className="animate-spin" /> Genero…
          </span>
        ) : "Genera la multipla"}
      </button>
    </div>
  );
}

function LegRow({ leg, color }) {
  const h = leg.home_team?.name || "Casa";
  const a = leg.away_team?.name || "Ospite";
  const alt = leg.alternative;
  return (
    <div
      data-testid={`ronin-leg-${leg.fixture_id}`}
      style={{
        display: "flex", flexDirection: "column", gap: "0.45rem",
        padding: "0.75rem 0.9rem",
        background: "rgba(12,14,16,0.65)",
        border: `1px solid ${color}33`,
        boxShadow: `inset 0 0 12px rgba(0,0,0,0.45)`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flex: 1, minWidth: 0 }}>
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontWeight: 800,
            fontSize: "0.6rem", color: color,
            padding: "0.1rem 0.35rem", border: `1px solid ${color}66`,
            letterSpacing: "0.1em",
          }}>{leg.grade}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.78rem", color: S.ink, letterSpacing: "0.02em",
              textTransform: "uppercase",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>{h} <span style={{ color: S.sub, fontWeight: 400 }}>vs</span> {a}</div>
            <div style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
              color: S.sub, marginTop: 1,
              display: "flex", alignItems: "center", gap: "0.4rem",
            }}>
              {leg.league_flag && <img src={leg.league_flag} alt="" style={{ width: 12, height: 8, objectFit: "cover" }} onError={(e) => e.target.style.display = "none"} />}
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{leg.league_name || "—"}</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.15rem" }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            fontSize: "0.82rem", color: color, textTransform: "uppercase",
            letterSpacing: "0.05em",
            textShadow: `0 0 6px ${color}55`,
            whiteSpace: "nowrap",
          }}>{leg.label}{leg.is_value ? " · V" : ""}</div>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.66rem", color: S.sub,
            }}>{leg.prob}%</span>
            <span style={{
              fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
              fontSize: "0.85rem", color: "var(--bc-ink, #fff)",
              padding: "0.1rem 0.45rem",
              background: `${color}22`, border: `1px solid ${color}55`,
            }}>{leg.odds.toFixed(2)}</span>
          </div>
        </div>
      </div>
      {alt && (
        <div
          data-testid={`ronin-leg-${leg.fixture_id}-alt`}
          style={{
            display: "flex", alignItems: "center", gap: "0.55rem",
            paddingLeft: "2.35rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
            color: S.sub,
          }}
        >
          <span style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.64rem", letterSpacing: "0.2em",
            color: `${color}cc`, textTransform: "uppercase",
          }}>oppure</span>
          <span style={{ color: S.ink, opacity: 0.9 }}>{alt.label}</span>
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem", color: S.sub,
          }}>· {alt.prob}%</span>
          <span style={{
            marginLeft: "auto",
            fontFamily: "'JetBrains Mono',monospace", fontWeight: 700,
            fontSize: "0.72rem", color: "var(--bc-ink, #fff)",
            padding: "0.05rem 0.4rem",
            background: `${color}18`, border: `1px solid ${color}40`,
          }}>{alt.odds.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}

function groupByDay(slips) {
  const byDay = new Map();
  for (const s of slips) {
    if (!s.saved_at) continue;
    const key = s.saved_at.slice(0, 10); // YYYY-MM-DD
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(s);
  }
  return [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

function friendlyDayLabel(ymd) {
  const today = new Date();
  const t = today.toISOString().slice(0, 10);
  const y = new Date(today.getTime() - 86_400_000).toISOString().slice(0, 10);
  if (ymd === t) return "Oggi";
  if (ymd === y) return "Ieri";
  // Italian long date (e.g. "lunedì 19 aprile")
  const d = new Date(ymd + "T12:00:00");
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
}

function dayAggregate(slips) {
  let won = 0, lost = 0, pending = 0;
  for (const s of slips) {
    if (s.status === "won") won += 1;
    else if (s.status === "lost") lost += 1;
    else pending += 1;
  }
  return { won, lost, pending };
}

function buildShareText(slip, { coach, target_odds, combined_odds, combined_prob, legs }) {
  // Accept both a saved slip (has status + leg.outcome) and the live multipla
  // payload (no status). Format a clean, readable recap for any messenger.
  const isSaved = !!slip;
  const ronin = (slip?.coach_label || coach || "Ronin").toString();
  const odds = (slip?.combined_odds ?? combined_odds ?? 0).toFixed(2);
  const prob = (slip?.combined_prob ?? combined_prob ?? 0).toFixed(1);
  const target = (slip?.target_odds ?? target_odds ?? 0).toFixed(1);
  const rows = (slip?.legs ?? legs ?? []).map((l, i) => {
    const home = l.home_team?.name || "?";
    const away = l.away_team?.name || "?";
    const outcomeMark =
      l.outcome === true ? " ✓" :
      l.outcome === false ? " ✗" :
      "";
    const score = (l.actual_home != null && l.actual_away != null)
      ? ` (${l.actual_home}-${l.actual_away})`
      : "";
    return `${i + 1}. ${home} vs ${away}${score}\n   ${l.label} @ ${Number(l.odds).toFixed(2)}${outcomeMark}`;
  }).join("\n");
  const statusLine = isSaved
    ? `\nStato: ${
        slip.status === "won"  ? "VINCENTE ✓" :
        slip.status === "lost" ? "PERDENTE ✗" : "in attesa"
      }`
    : "";
  return (
    `🥷 LA MULTIPLA DEL RONIN — ${ronin}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━━━\n` +
    `${rows}\n` +
    `━━━━━━━━━━━━━━━━━━━━━━━━\n` +
    `Quota totale: ${odds}x · Prob ${prob}% · Target ${target}x${statusLine}\n\n` +
    `— generata con Bet Cracker`
  );
}

async function shareSchedina({ slip = null, multipla = null, onFeedback }) {
  const text = buildShareText(slip, multipla || {});
  const title = slip
    ? `Schedina ${slip.coach_label} · ${slip.combined_odds?.toFixed(2)}x`
    : `Multipla Ronin · ${multipla?.combined_odds?.toFixed(2) || "—"}x`;
  // Prefer the native Web Share API (especially good on mobile)
  if (navigator.share) {
    try {
      await navigator.share({ title, text });
      onFeedback?.({ msg: "Schedina condivisa", kind: "ok" });
      return;
    } catch (err) {
      // User cancelled or share failed; fall through to clipboard
      if (err?.name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    onFeedback?.({ msg: "Schedina copiata negli appunti", kind: "ok" });
  } catch {
    onFeedback?.({ msg: "Impossibile condividere", kind: "ko" });
  }
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "In attesa", color: "var(--bc-sub, #9CA3AF)", glow: "rgba(156,163,175,0.35)", Icon: Clock },
    won:     { label: "Vincente", color: "#22FFA3", glow: "rgba(34,255,163,0.55)", Icon: Check },
    lost:    { label: "Perdente", color: "#FF4B5C", glow: "rgba(255,75,92,0.55)",  Icon: XIcon },
  };
  const cfg = map[status] || map.pending;
  const { Icon } = cfg;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "0.3rem",
      padding: "0.15rem 0.5rem",
      fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
      fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase",
      color: cfg.color,
      border: `1px solid ${cfg.color}66`,
      background: `${cfg.color}14`,
      boxShadow: `0 0 8px ${cfg.glow}`,
    }}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}

function HistorySlip({ slip, onDelete, onShare, onExport, exporting }) {
  const color = slip.coach_color || "#F6C643";
  const savedAt = slip.saved_at ? new Date(slip.saved_at) : null;
  const savedLabel = savedAt ? savedAt.toLocaleString("it-IT", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  }) : "—";
  return (
    <div
      data-testid={`ronin-history-${slip.id}`}
      style={{
        padding: "0.75rem 0.9rem",
        background: "rgba(12,14,16,0.7)",
        border: `1px solid ${color}33`,
        display: "flex", flexDirection: "column", gap: "0.55rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.55rem" }}>
        <RoninIcon coach={slip.coach} size={22} color={color} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
            fontSize: "0.82rem", letterSpacing: "0.22em", color: color,
            textShadow: `0 0 6px ${color}55`,
            textTransform: "uppercase",
          }}>{slip.coach_label} · {slip.n_legs} legs</div>
          <div style={{
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
            color: S.sub, marginTop: 1,
          }}>{savedLabel} · target {slip.target_odds}x</div>
        </div>
        <StatusBadge status={slip.status} />
        <span style={{
          fontFamily: "'JetBrains Mono',monospace", fontWeight: 800,
          fontSize: "1rem", color: color, textShadow: `0 0 8px ${color}77`,
        }}>{slip.combined_odds?.toFixed(2)}x</span>
        <button
          aria-label="Condividi schedina"
          data-testid={`ronin-history-share-${slip.id}`}
          onClick={() => onShare(slip)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: S.sub, padding: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = color)}
          onMouseLeave={(e) => (e.currentTarget.style.color = S.sub)}
        >
          <Share2 size={12} />
        </button>
        <button
          aria-label="Esporta come immagine"
          data-testid={`ronin-history-export-${slip.id}`}
          onClick={() => onExport && onExport(slip)}
          disabled={exporting}
          style={{
            background: "transparent", border: "none",
            cursor: exporting ? "wait" : "pointer",
            color: S.sub, padding: 2,
          }}
          onMouseEnter={(e) => { if (!exporting) e.currentTarget.style.color = color; }}
          onMouseLeave={(e) => { if (!exporting) e.currentTarget.style.color = S.sub; }}
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
        </button>
        <button
          aria-label="Elimina schedina"
          onClick={() => onDelete(slip.id)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: S.sub, padding: 2,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#FF4B5C")}
          onMouseLeave={(e) => (e.currentTarget.style.color = S.sub)}
        >
          <Trash2 size={12} />
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.28rem" }}>
        {(slip.legs || []).map((l, i) => {
          const outcome = l.outcome;
          const outcomeColor =
            outcome === true ? "#22FFA3" :
            outcome === false ? "#FF4B5C" :
            S.sub;
          const homeAway = `${l.home_team?.name || "?"} — ${l.away_team?.name || "?"}`;
          const score = (l.actual_home != null && l.actual_away != null)
            ? ` (${l.actual_home}-${l.actual_away})`
            : "";
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: "0.4rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.65rem",
              color: outcome == null ? S.ink : outcomeColor,
              opacity: outcome === false ? 0.85 : 1,
              textDecoration: outcome === false ? "line-through" : "none",
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: "50%",
                background: outcomeColor,
                boxShadow: outcome !== null ? `0 0 4px ${outcomeColor}` : "none",
                opacity: outcome === null ? 0.4 : 1,
                flexShrink: 0,
              }} />
              <span style={{
                flex: 1, whiteSpace: "nowrap",
                overflow: "hidden", textOverflow: "ellipsis",
              }}>{homeAway}{score} · <span style={{ color: color }}>{l.label}</span></span>
              <span>{(l.odds || 0).toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SamuraiRoninPage() {
  const navigate = useNavigate();
  const [activeCoach, setActiveCoach] = useState(null);
  const [targets, setTargets] = useState({ kurosawa: 3, mifune: 5, naruse: 10 });
  const [multipla, setMultipla] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveToast, setSaveToast] = useState(null); // {msg, kind}
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const savingLockRef = useRef(false);
  const exportRef = useRef(null);
  const [exportPayload, setExportPayload] = useState(null); // {slip}
  const [exporting, setExporting] = useState(null); // id key while busy
  const { isPremium } = usePremium();
  const { usage, increment } = useDailyUsage();

  const exportSlip = useCallback(async (slip, key = "current") => {
    if (!slip || !slip.legs?.length) return;
    setExporting(key);
    setExportPayload({ slip });
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      if (exportRef.current) {
        const tag = slip.coach || "ronin";
        const date = (slip.saved_at || new Date().toISOString()).slice(0, 10);
        await exportNodeToPng(exportRef.current, `bet-cracker-ronin-${tag}-${date}.png`);
      }
    } catch (e) {
      console.error("[Ronin] Export failed:", e);
    } finally {
      setExporting(null);
      setTimeout(() => setExportPayload(null), 200);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const cfg = _ownerHeaders();
    const ownerCode = cfg.headers["X-Access-Code"];
    try {
      const r = await axios.get(`${API}/ronin/slips`, cfg);
      let serverSlips = r.data?.slips || [];

      // Find slips THIS user has locally that the server has lost (DB reset,
      // fork, pod recycle, etc.) and rehydrate them. Scoped to current owner
      // — never resurrect another user's slips.
      const missing = findMissingOnServer(serverSlips, ownerCode);
      if (missing.length > 0) {
        await Promise.all(missing.map(async (s) => {
          try {
            await axios.post(`${API}/ronin/slips/rehydrate`, s, cfg);
          } catch {
            /* tombstoned / wrong owner / network — best-effort */
          }
        }));
        const r2 = await axios.get(`${API}/ronin/slips`, cfg);
        serverSlips = r2.data?.slips || serverSlips;
      }

      setHistory(serverSlips);
      reconcileLocalWithServer(serverSlips, ownerCode);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveSlip = useCallback(async () => {
    if (!multipla || !multipla.legs?.length) return;
    if (savingLockRef.current) return; // hard guard against double-clicks
    const cfg = _ownerHeaders();
    const ownerCode = cfg.headers["X-Access-Code"];
    savingLockRef.current = true;
    setSaving(true);
    try {
      const resp = await axios.post(`${API}/ronin/slips`, multipla, cfg);
      if (resp.data?.id) saveSlipLocal(resp.data, ownerCode);
      playSfx("coin");
      setSaveToast({ msg: "Schedina salvata nello storico", kind: "ok" });
      setTimeout(() => setSaveToast(null), 2500);
      loadHistory();
    } catch (e) {
      setSaveToast({ msg: "Errore durante il salvataggio", kind: "ko" });
      setTimeout(() => setSaveToast(null), 2500);
    } finally {
      setSaving(false);
      setTimeout(() => { savingLockRef.current = false; }, 1200);
    }
  }, [multipla, loadHistory]);

  const deleteSlip = useCallback(async (id) => {
    const cfg = _ownerHeaders();
    try {
      await axios.delete(`${API}/ronin/slips/${id}`, cfg);
      removeSlipLocal(id);
      loadHistory();
    } catch { /* silent */ }
  }, [loadHistory]);

  const showToast = useCallback(({ msg, kind }) => {
    setSaveToast({ msg, kind });
    setTimeout(() => setSaveToast(null), 2500);
  }, []);

  const shareSlip = useCallback((slip) => {
    shareSchedina({ slip, onFeedback: showToast });
  }, [showToast]);

  const shareCurrent = useCallback(() => {
    if (!multipla?.legs?.length) return;
    shareSchedina({ multipla, onFeedback: showToast });
  }, [multipla, showToast]);

  const [generatingFX, setGeneratingFX] = useState(null); // coach id while FX is running

  const FX_DURATION = { kurosawa: 2200, mifune: 1800, naruse: 1500 };

  const generate = useCallback(async (coachId) => {
    // Free tier daily cap: 1 ronin multipla/day.
    if (!isPremium) {
      try {
        await increment("ronin");
      } catch (err) {
        if (err?.response?.status === 429) {
          setError(err.response.data?.detail || "Hai esaurito la multipla giornaliera (Free). Sblocca Premium per uso illimitato.");
          return;
        }
      }
    }
    setActiveCoach(coachId);
    setLoading(true);
    setError(null);
    setMultipla(null);
    setGeneratingFX(coachId);
    const started = Date.now();
    try {
      const r = await axios.get(`${API}/ronin/multipla`, {
        params: {
          coach: coachId,
          target: targets[coachId],
          _r: Math.random().toString(36).slice(2, 10), // cache-buster
        },
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      });
      // Ensure the whole animation plays even if the API responds in a flash.
      const elapsed = Date.now() - started;
      const waitExtra = Math.max(0, FX_DURATION[coachId] - elapsed);
      setTimeout(() => {
        setMultipla({ coach: coachId, ...r.data });
        setGeneratingFX(null);
        setLoading(false);
      }, waitExtra);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore generazione multipla.");
      setGeneratingFX(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, isPremium]);

  // No auto-generation on mount: the user must pick a coach manually.

  const activeColor = COACHES.find((c) => c.id === (multipla?.coach || activeCoach))?.color || "#F6C643";

  return (
    <div data-testid="ronin-page" style={{ minHeight: "100vh", background: S.bg, color: S.ink }}>
      {/* Custom slider thumb styling (scoped to .ronin-slider) */}
      <style>{`
        .ronin-slider::-webkit-slider-thumb {
          appearance: none;
          width: 18px; height: 18px;
          border-radius: 50%;
          background: #0a0c0e;
          border: 2px solid currentColor;
          box-shadow: 0 0 10px currentColor, 0 0 20px currentColor;
          cursor: pointer;
          transition: transform 180ms cubic-bezier(0.2, 0.8, 0.15, 1), box-shadow 180ms ease;
        }
        .ronin-slider::-webkit-slider-thumb:hover { transform: scale(1.25); }
        .ronin-slider::-webkit-slider-thumb:active { transform: scale(1.4); }
        .ronin-slider::-moz-range-thumb {
          width: 18px; height: 18px;
          border-radius: 50%;
          background: #0a0c0e;
          border: 2px solid currentColor;
          box-shadow: 0 0 10px currentColor;
          cursor: pointer;
        }
        @keyframes roninAppear {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <SamuraiBackground />
      <RoninGenerateFX coach={generatingFX} active={!!generatingFX} />
      <Navbar />

      <div style={{ position: "relative", maxWidth: 960, margin: "0 auto", padding: "1.5rem 1.25rem 5rem", zIndex: 2 }}>
        <button
          data-testid="ronin-back-btn"
          onClick={() => navigate("/samurai")}
          style={{
            display: "inline-flex", alignItems: "center", gap: "0.4rem",
            background: "transparent", border: "none",
            color: S.sub, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
            padding: "0.3rem 0", cursor: "pointer", marginBottom: "1.25rem",
            letterSpacing: "0.1em",
          }}
        >
          <ArrowLeft size={13} /> Torna al Samurai Picker
        </button>

        {/* Free-tier daily quota banner */}
        {!isPremium && usage && (
          <div
            data-testid="ronin-quota-banner"
            onClick={() => { if (usage.ronin.remaining === 0) navigate("/pricing"); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, padding: "0.7rem 1rem", marginBottom: "1.2rem",
              background: usage.ronin.remaining === 0 ? "rgba(239,68,68,0.08)" : "rgba(200,255,0,0.04)",
              border: `1px solid ${usage.ronin.remaining === 0 ? "rgba(239,68,68,0.45)" : "rgba(200,255,0,0.4)"}`,
              color: usage.ronin.remaining === 0 ? "#EF4444" : "#C8FF00",
              cursor: usage.ronin.remaining === 0 ? "pointer" : "default",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.74rem",
            }}
          >
            <span>
              ⚡ Multipla del Ronin · {usage.ronin.used}/{usage.ronin.limit} oggi · Free
            </span>
            <span style={{
              padding: "0.25rem 0.6rem",
              border: "1px solid currentColor", borderRadius: 2,
              fontSize: "0.62rem", letterSpacing: "0.16em", textTransform: "uppercase",
              fontWeight: 700,
            }}>
              {usage.ronin.remaining === 0 ? "Sblocca Premium →" : "Premium illimitato →"}
            </span>
          </div>
        )}

        {/* HERO */}
        <div style={{ textAlign: "center", marginBottom: "1.8rem" }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "0.55rem",
            padding: "0.4rem 1rem",
            background: "rgba(30,30,36,0.55)",
            border: `1px solid ${S.hairline2}`,
            marginBottom: "0.9rem",
          }}>
            <Sparkles size={11} style={{ color: "#F6C643" }} />
            <span style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.7rem", letterSpacing: "0.3em", color: S.sub,
              textTransform: "uppercase",
            }}>La multipla del Ronin</span>
          </div>
          <h1 style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
            fontSize: "clamp(1.8rem, 4.2vw, 2.8rem)", lineHeight: 1,
            letterSpacing: "-0.01em", margin: "0 0 0.5rem",
          }}>
            SCEGLI IL TUO <span style={{ color: "#F6C643", textShadow: "0 0 18px rgba(246,198,67,0.55)" }}>RONIN</span>.
          </h1>
          <p style={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem",
            color: S.sub, maxWidth: 560, margin: "0 auto",
          }}>
            Tre samurai senza padrone, tre filosofie. Ognuno costruisce la sua multipla
            con le regole del suo dojo e la quota target che decidi tu.
          </p>
        </div>

        <KatanaDivider style={{ marginBottom: "1.25rem" }} />

        {/* COACH SELECTOR */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "0.85rem", marginBottom: "2rem",
        }}>
          {COACHES.map((c) => (
            <CoachCard
              key={c.id}
              coach={c}
              active={(multipla?.coach || activeCoach) === c.id}
              targetOdds={targets[c.id]}
              setTargetOdds={(v) => setTargets((t) => ({ ...t, [c.id]: v }))}
              onGenerate={() => generate(c.id)}
              loading={loading && activeCoach === c.id}
              disabled={loading}
            />
          ))}
        </div>

        {/* RESULT */}
        {error && (
          <div data-testid="ronin-error" style={{
            padding: "0.85rem 1rem",
            background: "rgba(255,75,92,0.1)", border: `1px solid ${S.blood}66`,
            color: "#FFB4BC", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
            display: "flex", gap: "0.6rem", alignItems: "flex-start",
          }}>
            <AlertTriangle size={13} style={{ color: S.blood, flexShrink: 0, marginTop: 2 }} />
            {error}
          </div>
        )}

        {multipla && !loading && (
          <div data-testid="ronin-multipla-result" style={{
            animation: "roninAppear 350ms cubic-bezier(0.2, 0.8, 0.15, 1) both",
          }}>
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", gap: "0.75rem",
              padding: "0.9rem 1rem",
              background: `linear-gradient(135deg, rgba(10,12,14,0.95) 0%, ${activeColor}11 50%, rgba(10,12,14,0.95) 100%)`,
              border: `1px solid ${activeColor}`,
              boxShadow: `0 0 18px -4px ${activeColor}66`,
            }}>
              <RoninIcon coach={multipla.coach} size={32} color={activeColor} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                  fontSize: "0.85rem", letterSpacing: "0.22em", color: activeColor,
                  textTransform: "uppercase", textShadow: `0 0 8px ${activeColor}66`,
                }}>{multipla.coach_label} · {multipla.n_legs} legs</div>
                <div style={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem",
                  color: S.sub, fontStyle: "italic",
                }}>{multipla.coach_sub} · target {multipla.target_odds}x</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace", fontWeight: 800,
                  fontSize: "1.6rem", color: activeColor,
                  textShadow: `0 0 14px ${activeColor}77`,
                  lineHeight: 1,
                }}>{multipla.combined_odds?.toFixed(2) || "—"}x</div>
                <div style={{
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.62rem",
                  color: S.sub, marginTop: 2,
                }}>prob {multipla.combined_prob}%</div>
              </div>
            </div>

            {/* Warnings */}
            {multipla.warnings?.length > 0 && (
              <div style={{
                padding: "0.55rem 0.9rem", marginTop: "0.5rem",
                background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.35)",
                color: "#FCD34D", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
                display: "flex", gap: "0.5rem", alignItems: "center",
              }}>
                <AlertTriangle size={12} style={{ color: "#FCD34D", flexShrink: 0 }} />
                {multipla.warnings[0]}
              </div>
            )}

            {/* Legs */}
            <div style={{ marginTop: "0.8rem", display: "flex", flexDirection: "column", gap: "0.55rem" }}>
              {(multipla.legs || []).map((leg) => (
                <LegRow key={`${leg.fixture_id}-${leg.market}`} leg={leg} color={activeColor} />
              ))}
            </div>

            {multipla.legs?.length === 0 && (
              <div style={{
                marginTop: "1rem", padding: "1.2rem",
                background: "rgba(30,30,36,0.55)", border: `1px solid ${S.hairline2}`,
                textAlign: "center",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: S.sub,
              }}>
                {multipla.message || "Nessuna multipla disponibile con questi parametri."}
              </div>
            )}

            {/* Save + Share buttons */}
            {multipla.legs?.length > 0 && (
              <div style={{ display: "flex", gap: "0.6rem", marginTop: "1rem" }}>
                <button
                  data-testid="ronin-save-slip"
                  onClick={saveSlip}
                  disabled={saving}
                  style={{
                    flex: 1,
                    padding: "0.7rem 1rem",
                    background: saving ? "transparent" : `${activeColor}14`,
                    border: `1px solid ${activeColor}`,
                    color: activeColor,
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                    fontSize: "0.78rem", letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    cursor: saving ? "wait" : "pointer",
                    textShadow: `0 0 8px ${activeColor}55`,
                    boxShadow: `0 0 14px -4px ${activeColor}55`,
                    transition: "all 200ms ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.currentTarget.style.background = `${activeColor}22`;
                      e.currentTarget.style.boxShadow = `0 0 22px -2px ${activeColor}77`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.currentTarget.style.background = `${activeColor}14`;
                      e.currentTarget.style.boxShadow = `0 0 14px -4px ${activeColor}55`;
                    }
                  }}
                >
                  {saving ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", justifyContent: "center" }}>
                      <Loader2 size={12} className="animate-spin" /> Salvataggio…
                    </span>
                  ) : (
                    <span>Salva schedina</span>
                  )}
                </button>
                <button
                  data-testid="ronin-share-current"
                  onClick={shareCurrent}
                  aria-label="Condividi schedina"
                  style={{
                    padding: "0.7rem 1rem",
                    background: "transparent",
                    border: `1px solid ${S.hairline2}`,
                    color: S.sub,
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                    fontSize: "0.72rem", letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: "0.45rem",
                    transition: "all 180ms ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = activeColor; e.currentTarget.style.borderColor = activeColor; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = S.sub; e.currentTarget.style.borderColor = S.hairline2; }}
                >
                  <Share2 size={12} /> Condividi
                </button>
                <button
                  data-testid="ronin-export-current"
                  onClick={() => exportSlip(multipla, "current")}
                  disabled={exporting === "current"}
                  aria-label="Esporta schedina come immagine"
                  style={{
                    padding: "0.7rem 1rem",
                    background: "transparent",
                    border: `1px solid ${S.hairline2}`,
                    color: S.sub,
                    fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                    fontSize: "0.72rem", letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    cursor: exporting === "current" ? "wait" : "pointer",
                    display: "inline-flex", alignItems: "center", gap: "0.45rem",
                    transition: "all 180ms ease",
                  }}
                  onMouseEnter={(e) => { if (exporting !== "current") { e.currentTarget.style.color = activeColor; e.currentTarget.style.borderColor = activeColor; } }}
                  onMouseLeave={(e) => { if (exporting !== "current") { e.currentTarget.style.color = S.sub; e.currentTarget.style.borderColor = S.hairline2; } }}
                >
                  {exporting === "current"
                    ? <Loader2 size={12} className="animate-spin" />
                    : <Download size={12} />}
                  Immagine
                </button>
              </div>
            )}
          </div>
        )}

        {/* Toast for save feedback */}
        {saveToast && (
          <div
            data-testid="ronin-save-toast"
            style={{
              position: "fixed", top: 80, left: "50%",
              transform: "translateX(-50%)",
              zIndex: 100,
              padding: "0.6rem 1rem",
              background: "rgba(10,12,14,0.96)",
              border: `1px solid ${saveToast.kind === "ok" ? "#22FFA3" : "#FF4B5C"}`,
              color: saveToast.kind === "ok" ? "#22FFA3" : "#FF4B5C",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem",
              fontWeight: 700, letterSpacing: "0.1em",
              boxShadow: `0 0 20px ${saveToast.kind === "ok" ? "rgba(34,255,163,0.5)" : "rgba(255,75,92,0.5)"}`,
              animation: "roninAppear 220ms cubic-bezier(0.2, 0.8, 0.15, 1)",
            }}
          >
            {saveToast.msg}
          </div>
        )}

        {/* HISTORY section */}
        <div style={{ marginTop: "2.5rem" }} data-testid="ronin-history-section">
          <button
            data-testid="ronin-history-toggle"
            onClick={() => setHistoryOpen((v) => !v)}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", gap: "0.55rem",
              background: "rgba(12,14,16,0.7)",
              border: `1px solid ${S.hairline2}`,
              padding: "0.7rem 0.9rem",
              color: S.ink,
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
              fontSize: "0.85rem", letterSpacing: "0.25em",
              textTransform: "uppercase",
              cursor: "pointer",
              transition: "all 180ms ease",
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#F6C643"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = S.hairline2}
          >
            <Clock size={13} style={{ color: "#F6C643" }} />
            Storico Schedine
            <span style={{
              marginLeft: "auto",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
              fontWeight: 800, color: S.sub, letterSpacing: "0.05em",
            }}>
              {history.length === 0 ? "vuoto" : `${history.length} salvate`}
              {"  "}{historyOpen ? "▲" : "▼"}
            </span>
          </button>
          {historyOpen && (
            <div style={{ marginTop: "0.6rem", display: "flex", flexDirection: "column", gap: "0.9rem" }}>
              {historyLoading && <Loader2 size={14} className="animate-spin" style={{ color: S.sub, margin: "1rem auto" }} />}
              {!historyLoading && history.length === 0 && (
                <div style={{
                  padding: "1.25rem",
                  background: "rgba(12,14,16,0.7)",
                  border: `1px solid ${S.hairline2}`,
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
                  color: S.sub, textAlign: "center",
                }}>
                  Ancora nessuna schedina salvata. Genera una multipla e premi <strong style={{ color: S.ink }}>Salva schedina</strong> per seguirne l'esito.
                </div>
              )}
              {groupByDay(history).map(([ymd, slips]) => {
                const agg = dayAggregate(slips);
                return (
                  <div key={ymd} data-testid={`ronin-history-day-${ymd}`}>
                    {/* Day divider */}
                    <div style={{
                      display: "flex", alignItems: "center", gap: "0.55rem",
                      padding: "0.35rem 0", marginBottom: "0.5rem",
                      borderBottom: `1px solid ${S.hairline2}`,
                    }}>
                      <span style={{
                        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
                        fontSize: "0.78rem", letterSpacing: "0.22em",
                        color: "#F6C643", textTransform: "uppercase",
                        textShadow: "0 0 6px rgba(246,198,67,0.5)",
                      }}>{friendlyDayLabel(ymd)}</span>
                      <span style={{
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.58rem",
                        color: S.sub,
                      }}>· {slips.length} schedina{slips.length !== 1 ? "e" : ""}</span>
                      <span style={{
                        marginLeft: "auto", display: "flex", gap: "0.5rem",
                        fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
                        fontWeight: 700,
                      }}>
                        {agg.won > 0 && <span style={{ color: "#22FFA3" }}>✓ {agg.won}</span>}
                        {agg.lost > 0 && <span style={{ color: "#FF4B5C" }}>✗ {agg.lost}</span>}
                        {agg.pending > 0 && <span style={{ color: "var(--bc-sub, #9CA3AF)" }}>◷ {agg.pending}</span>}
                      </span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      {slips.map((slip) => (
                        <HistorySlip
                          key={slip.id}
                          slip={slip}
                          onDelete={deleteSlip}
                          onShare={shareSlip}
                          onExport={(s) => exportSlip(s, s.id)}
                          exporting={exporting === slip.id}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Hidden export render target — moved off-screen rather than display:none
          so html-to-image actually has a painted node to snapshot. */}
      {exportPayload && (
        <div style={{
          position: "fixed", left: -10000, top: 0,
          pointerEvents: "none", opacity: 0,
        }} aria-hidden>
          <BetSlipExportCard
            ref={exportRef}
            mode="ronin"
            slip={exportPayload.slip}
          />
        </div>
      )}
    </div>
  );
}
